import { useMutation } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Button } from '@givebutter/primitives/button';
import { useToast } from '@givebutter/primitives/toast';
import { deleteCampaignInviteById } from 'api/campaigns';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useForceRender } from 'customHooks/useForceRender';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { queryClient } from 'queries/queryClient';
import { GivebutterAxiosError } from 'types/error';

interface DeletePendingInviteDialogProps extends AlertDialog.AlertDialogRootProps {
    inviteId: string;
}

const DeletePendingInviteDialog = (props: DeletePendingInviteDialogProps) => {
    const { forceRender, key } = useForceRender();
    const { error, success } = useToast();
    const { children, inviteId, ...rest } = props;
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign.id;

    const { mutate: deletePendingInvite } = useMutation({
        mutationFn: (inviteId: string) => deleteCampaignInviteById(campaignId, inviteId),
        onSuccess: () => {
            success('Successfully deleted pending invite!');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.invites(campaignId),
            });
            forceRender();
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to delete pending invite'),
    });

    return (
        <AlertDialog.Root {...rest} key={key} type="warning">
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>Delete pending invite</AlertDialog.Title>
                        <AlertDialog.Description>
                            You cannot undo this action.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                        <Button
                            variant="solid"
                            color={'error'}
                            onClick={() => deletePendingInvite(inviteId)}
                            className="w-1/2"
                        >
                            Delete invite
                        </Button>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DeletePendingInviteDialogTrigger = AlertDialog.Trigger;

const Root = DeletePendingInviteDialog;
const Trigger = DeletePendingInviteDialogTrigger;

export { DeletePendingInviteDialog, DeletePendingInviteDialogTrigger, Root, Trigger };
